import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * PaperVerse Writer feature flag.
 *
 * Writer is on for everyone — including signed-out visitors, who see the
 * prompt-first guest landing at /write and therefore also need the flag.
 * An admin can still kill the feature without a deploy by setting the
 * `writer_ga_enabled` row in `app_config` to `false`; the server-side
 * `is_writer_enabled()` function reads that row and defaults to on when
 * it is absent. The function is granted to anon for exactly this read.
 */
export function useWriterEnabled(): { enabled: boolean; loading: boolean } {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        // Fail open: if the call errors, surface Writer rather than lock
        // everyone out of a live feature because of a transient RPC fault.
        const { data, error } = await supabase.rpc('is_writer_enabled');
        if (cancelled) return;
        setEnabled(error ? true : data === true);
      } catch {
        if (!cancelled) setEnabled(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    check();

    return () => {
      cancelled = true;
    };
  }, []);

  return { enabled, loading };
}
